import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import BaseTile from "../../../components/Tile/BaseTile";
import TileContent, { TileStatusContent } from "../../../components/Tile/TileContent";
import { useLocalFilters } from "../../../hooks/useLocalFilters";
import { useAutoLoad } from "../../../hooks/useAutoLoad";
import { useOcelMetadata } from "../../../stores/OcelContext";
import TileSettingsModal from "../../../components/common/TileSettingsModal";
import LocalFiltersActiveBadge from "../../../components/common/LocalFiltersActiveBadge";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../../../components/ui/tooltip";
import { createFilteredRequest, makePostRequest } from "../../../services/apiClient";
import ObjectCountKPISettings from "./ObjectCountKPISettings";
import { resolveAmpelColor } from "../EfficiencyKPI/formula";
import {
  ALL_TIME_PRESET,
  resolveCurrentRange,
  resolvePreviousRange,
  formatRangeLabel,
  formatComparisonCaption,
  detectPeriodPresetFromDateRange,
} from "../_shared/periodPresets";
import {
  TREND_BADGE_CLASSES,
  KPIValueRow,
  KPIValueColumn,
  KPIValueSlot,
  KPITrendSlot,
  KPIBigValue,
  KPICaption,
  KPITrendBadge,
} from "../_shared/kpiTrendDisplay";

const formatCount = (num) => Number(num).toLocaleString(undefined, { maximumFractionDigits: 0 });

const formatTrendPercent = (trend) => {
  // Vorperiode war 0 — eine prozentuale Änderung ist mathematisch nicht
  // definiert (Division durch 0), also explizit als "kein Wert" kennzeichnen
  // statt eine nackte, einheitenlose Zahl zu zeigen, die leicht mit einem
  // Prozentwert verwechselt wird.
  if (trend.percent === null) return "Kein Wert";
  const sign = trend.percent > 0 ? "+" : trend.percent < 0 ? "−" : "";
  return `${sign}${Math.abs(trend.percent).toFixed(1)} %`;
};

const ObjectCountKPI = ({
  activeFile,
  onRefresh,
  onGetHeaderActions,
  onTitleChange,
  onRegisterSettingsGetter,
  initialSettings,
}) => {
  const { t } = useTranslation();
  const {
    localFilters,
    updateLocalFilter,
    clearLocalFilters,
    hasLocalFilters,
    localFilterCount,
    currentFilters,
    globalFilterState,
    startDraftMode,
    applyDraftFilters,
    cancelDraftFilters,
  } = useLocalFilters(activeFile, initialSettings?.localFilters ?? null);

  const metadata = useOcelMetadata(activeFile);

  // Component configuration. The fields the first fetch depends on start from
  // the saved config synchronously: the mount-time auto-load runs before the
  // restore effect below has re-rendered, and would otherwise fire one request
  // with the defaults (all object types, all time) whose response could land
  // after — and overwrite — the correct one.
  const saved = (key, fallback) => (initialSettings?.[key] !== undefined ? initialSettings[key] : fallback);
  const [componentTitle, setComponentTitle] = useState("");
  const [objectType, setObjectType] = useState(() => saved("objectType", ""));
  const [ampelEnabled, setAmpelEnabled] = useState(false);
  const [ampelLower, setAmpelLower] = useState(10);
  const [ampelUpper, setAmpelUpper] = useState(100);
  const [ampelColorRed, setAmpelColorRed] = useState("#ef4444");
  const [ampelColorYellow, setAmpelColorYellow] = useState("#eab308");
  const [ampelColorGreen, setAmpelColorGreen] = useState("#22c55e");

  // Betrachtungszeitraum / Vorperioden-Vergleich configuration
  const [periodPreset, setPeriodPreset] = useState(() => (
    initialSettings
      ? saved("periodPreset", detectPeriodPresetFromDateRange(initialSettings.localFilters?.dateRange))
      : ALL_TIME_PRESET.key
  ));
  const [customStart, setCustomStart] = useState(() => saved("customStart", null));
  const [customEnd, setCustomEnd] = useState(() => saved("customEnd", null));
  const [showPeriodLabel, setShowPeriodLabel] = useState(true);
  const [comparisonEnabled, setComparisonEnabled] = useState(() => saved("comparisonEnabled", false));
  const [trendDirection, setTrendDirection] = useState("higherIsBetter");

  // Data state
  const [kpiData, setKpiData] = useState(null);
  const [previousKpiData, setPreviousKpiData] = useState(null);
  const [resolvedRange, setResolvedRange] = useState(null);
  const [resolvedPreviousRange, setResolvedPreviousRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [pendingApply, setPendingApply] = useState(false);

  // Modal
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  // Derived metadata — availableObjectTypes filtered by global object-type filter only
  // (local objectTypes filter must not restrict settings dropdown options)
  const [availableObjectTypes, setAvailableObjectTypes] = useState([]);

  useEffect(() => {
    if (!metadata?.object_types) return;
    const allTypes = Object.keys(metadata.object_types).sort();
    const filtered =
      globalFilterState?.objectTypes?.length > 0
        ? allTypes.filter((t) => globalFilterState.objectTypes.includes(t))
        : allTypes;
    setAvailableObjectTypes(filtered);
    // Auto-reset if selected type is no longer available under global filters
    if (objectType && !filtered.includes(objectType)) {
      setObjectType(filtered[0] ?? "");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilterState?.objectTypes, metadata]);

  // Restore settings from saved config (mount-only)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialSettings) return;
    if (initialSettings.componentTitle !== undefined) setComponentTitle(initialSettings.componentTitle);
    if (initialSettings.objectType !== undefined) setObjectType(initialSettings.objectType);
    if (initialSettings.ampelEnabled !== undefined) setAmpelEnabled(initialSettings.ampelEnabled);
    if (initialSettings.ampelLower !== undefined) setAmpelLower(initialSettings.ampelLower);
    if (initialSettings.ampelUpper !== undefined) setAmpelUpper(initialSettings.ampelUpper);
    if (initialSettings.ampelColorRed !== undefined) setAmpelColorRed(initialSettings.ampelColorRed);
    if (initialSettings.ampelColorYellow !== undefined) setAmpelColorYellow(initialSettings.ampelColorYellow);
    if (initialSettings.ampelColorGreen !== undefined) setAmpelColorGreen(initialSettings.ampelColorGreen);
    if (initialSettings.periodPreset !== undefined) {
      setPeriodPreset(initialSettings.periodPreset);
    } else {
      // Configs saved before periodPreset existed — derive a best-guess preset
      // from the legacy resolved dateRange so old tiles keep working unchanged.
      setPeriodPreset(detectPeriodPresetFromDateRange(initialSettings.localFilters?.dateRange));
    }
    if (initialSettings.customStart !== undefined) setCustomStart(initialSettings.customStart);
    if (initialSettings.customEnd !== undefined) setCustomEnd(initialSettings.customEnd);
    if (initialSettings.showPeriodLabel !== undefined) setShowPeriodLabel(initialSettings.showPeriodLabel);
    if (initialSettings.comparisonEnabled !== undefined) setComparisonEnabled(initialSettings.comparisonEnabled);
    if (initialSettings.trendDirection !== undefined) setTrendDirection(initialSettings.trendDirection);
    setPendingApply(true);
  }, []);

  // Register settings getter with parent so it can be collected on Save
  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({
      componentTitle, objectType, localFilters,
      ampelEnabled, ampelLower, ampelUpper, ampelColorRed, ampelColorYellow, ampelColorGreen,
      periodPreset, customStart, customEnd, showPeriodLabel, comparisonEnabled, trendDirection,
    }));
  }, [
    componentTitle, objectType, localFilters,
    ampelEnabled, ampelLower, ampelUpper, ampelColorRed, ampelColorYellow, ampelColorGreen,
    periodPreset, customStart, customEnd, showPeriodLabel, comparisonEnabled, trendDirection,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Settings modal lifecycle ───────────────────────────────────────────────
  const handleOpenSettings = useCallback(() => {
    startDraftMode();
    setIsSettingsModalOpen(true);
  }, [startDraftMode]);

  const handleSettingsOpenChange = useCallback(
    (val) => {
      setIsSettingsModalOpen(val);
      if (!val) cancelDraftFilters();
    },
    [cancelDraftFilters]
  );

  // ── Data fetching ──────────────────────────────────────────────────────────
  // Only the most recently started fetch may commit its result — a slower,
  // older request (e.g. superseded by a settings/filter change) must not
  // overwrite the newer one.
  const fetchSeqRef = useRef(0);
  const fetchData = useCallback(async () => {
    if (!activeFile) return;
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    setHasFetched(true);
    try {
      const now = new Date();
      const currentRange = resolveCurrentRange(periodPreset, { customStart, customEnd }, now);
      const previousRange = comparisonEnabled
        ? resolvePreviousRange(periodPreset, currentRange, now)
        : null;

      // A non-"all" period is itself a local override, same as any other
      // local filter — decide local-vs-global the same way useLocalFilters
      // does, but without depending on this render's (possibly stale) value.
      const usesLocalFilters = hasLocalFilters || periodPreset !== ALL_TIME_PRESET.key;
      const filterBase = usesLocalFilters ? localFilters : globalFilterState;

      // selected_attributes is intentionally omitted: aggregations.total_objects
      // (the only field this tile reads) is computed independent of it, and
      // omitting it here keeps this request's cache key identical to the
      // backend warm mapper's (_warm_object_count_kpi), which cannot know a
      // per-tile pinned attribute at planning time (metadata isn't loaded
      // yet when warm targets are built) — pinning one here would silently
      // desync the two and make warming a no-op for this tile.
      const baseRequest = {
        file_type: activeFile,
        object_type: objectType || undefined,
        limit: 1,
        offset: 0,
      };

      const currentRequest = createFilteredRequest(baseRequest, { ...filterBase, dateRange: currentRange });
      const fetches = [makePostRequest("/ocel/object-table", currentRequest, {}, "Object Count KPI")];

      if (previousRange) {
        const previousRequest = createFilteredRequest(baseRequest, { ...filterBase, dateRange: previousRange });
        fetches.push(makePostRequest("/ocel/object-table", previousRequest, {}, "Object Count KPI (Vorperiode)"));
      } else {
        fetches.push(Promise.resolve(null));
      }

      const [currentResponse, previousResponse] = await Promise.all(fetches);
      if (seq !== fetchSeqRef.current) return;
      setKpiData(currentResponse);
      setPreviousKpiData(previousResponse);
      setResolvedRange(currentRange);
      setResolvedPreviousRange(previousRange);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      setError(err?.message || "Failed to load object count");
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [
    activeFile, objectType, periodPreset, customStart, customEnd,
    comparisonEnabled, hasLocalFilters, localFilters, globalFilterState,
  ]);

  // Fire after Apply commits new config + filters
  useEffect(() => {
    if (!pendingApply) return;
    setPendingApply(false);
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingApply]);

  // Auto-reload when filters or active file changes (after first load)
  useEffect(() => {
    if (hasFetched) fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilters, activeFile]);

  useAutoLoad(fetchData, [activeFile, currentFilters], kpiData);

  useEffect(() => {
    if (onRefresh) onRefresh(fetchData);
  }, [onRefresh, fetchData]);

  // A non-"all" period is a local override in its own right, but it no longer
  // lives inside localFilters.dateRange (see fetchData) — reflect it in the
  // badge here instead of writing it back into hook state.
  const periodIsLocalOverride = periodPreset !== ALL_TIME_PRESET.key;
  const displayHasLocalFilters = hasLocalFilters || periodIsLocalOverride;
  const displayLocalFilterCount = localFilterCount + (periodIsLocalOverride ? 1 : 0);

  // ── Header actions ─────────────────────────────────────────────────────────
  const headerActions = useMemo(
    () => (
      <div className="flex items-center gap-2">
        {displayHasLocalFilters && (
          <LocalFiltersActiveBadge
            hasLocalFilters={displayHasLocalFilters}
            localFilterCount={displayLocalFilterCount}
            onClearLocalFilters={clearLocalFilters}
          />
        )}
        <button
          onClick={handleOpenSettings}
          className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label={t('common:actions.openSettings')}
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="text-xs">{t('common:actions.settings')}</span>
        </button>
      </div>
    ),
    [handleOpenSettings, displayHasLocalFilters, displayLocalFilterCount, clearLocalFilters]
  );

  useEffect(() => {
    if (onGetHeaderActions) onGetHeaderActions(headerActions);
  }, [onGetHeaderActions, headerActions]);

  const displayTitle = componentTitle || "Object Count KPI";

  useEffect(() => {
    if (onTitleChange) onTitleChange(displayTitle);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayHasLocalFilters, displayTitle]); // Re-run when badge appears/disappears, or when a restored title is applied

  // ── Derived display values ─────────────────────────────────────────────────
  const objectCount = kpiData?.aggregations?.total_objects;
  const hasCount = objectCount !== null && objectCount !== undefined;

  // "Gesamter Zeitraum" has no meaningful Vorperiode at all — treat comparison
  // as off regardless of the raw stored flag, so a tile switched to this
  // preset (without the user separately unchecking the box, which the
  // Settings UI already disables/greys out) doesn't show a dangling "Kein
  // Vergleichswert verfügbar" placeholder for a comparison that was never
  // possible in the first place.
  const effectiveComparisonEnabled = comparisonEnabled && periodPreset !== ALL_TIME_PRESET.key;

  const previousObjectCount = previousKpiData?.aggregations?.total_objects;
  const hasPreviousValue = effectiveComparisonEnabled && previousObjectCount !== null && previousObjectCount !== undefined;

  const periodLabel = showPeriodLabel && resolvedRange
    ? formatRangeLabel(resolvedRange)
    : null;

  const previousPeriodLabel = resolvedPreviousRange
    ? formatRangeLabel(resolvedPreviousRange)
    : null;

  const comparisonCaption = previousPeriodLabel
    ? formatComparisonCaption(periodPreset, previousPeriodLabel)
    : null;

  const trend = useMemo(() => {
    if (!effectiveComparisonEnabled || !hasCount || !hasPreviousValue) return null;
    const diff = objectCount - previousObjectCount;
    // Vorperiode war 0 — keine sinnvolle prozentuale Richtung berechenbar,
    // also neutral darstellen statt eine (nicht belastbare) Verbesserung/
    // Verschlechterung zu suggerieren. formatTrendPercent zeigt dafür
    // "Kein Wert" statt einer Prozentzahl.
    if (previousObjectCount === 0) {
      return { diff, direction: "flat", color: "neutral", percent: null };
    }
    const direction = diff > 0 ? "up" : diff < 0 ? "down" : "flat";
    const improved = trendDirection === "higherIsBetter" ? diff > 0 : diff < 0;
    const worsened = trendDirection === "higherIsBetter" ? diff < 0 : diff > 0;
    const color = diff === 0 ? "neutral" : improved ? "positive" : worsened ? "negative" : "neutral";
    const percent = (diff / Math.abs(previousObjectCount)) * 100;
    return { diff, direction, color, percent };
  }, [effectiveComparisonEnabled, hasCount, hasPreviousValue, objectCount, previousObjectCount, trendDirection]);

  const ampelColor = useMemo(() => {
    if (!ampelEnabled || !hasCount) return null;
    return resolveAmpelColor(objectCount, {
      lower: ampelLower,
      upper: ampelUpper,
      colorRed: ampelColorRed,
      colorYellow: ampelColorYellow,
      colorGreen: ampelColorGreen,
    });
  }, [ampelEnabled, hasCount, objectCount, ampelLower, ampelUpper, ampelColorRed, ampelColorYellow, ampelColorGreen]);

  // When the active Ampel zone is red/green, match the trend pill's own
  // bg-x-50/text-x-700 palette exactly, regardless of which hex the user
  // configured for that zone — keeps the two indicators visually consistent.
  const ampelBadgeClass =
    ampelColor === ampelColorGreen ? TREND_BADGE_CLASSES.positive
    : ampelColor === ampelColorRed ? TREND_BADGE_CLASSES.negative
    : null;

  return (
    <BaseTile loading={loading} error={error} minHeight="sm" padding="sm">
      {/* Placeholder when not yet configured */}
      {!hasFetched && !loading && !error && (
        <TileStatusContent
          status="info"
          title="Configure Object Count KPI"
          message="Click the gear icon above to select an object type and configure this tile."
          icon={
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
        />
      )}

      {/* KPI display */}
      {hasFetched && !loading && !error && kpiData && (
        <TileContent align="stretch" justify="start">
          <div className="w-full flex flex-col items-center gap-1 py-1">
            <TooltipProvider>
              <div className="text-sm font-medium text-gray-700">{displayTitle}</div>

              <KPIValueRow>
                <KPIValueColumn>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <KPIValueSlot className="cursor-default">
                        <KPIBigValue
                          className={ampelColor ? `rounded-lg px-3 py-0.5 ${ampelBadgeClass || ""}` : undefined}
                          style={ampelColor && !ampelBadgeClass ? { backgroundColor: `${ampelColor}26`, color: ampelColor } : undefined}
                          rawValue={objectCount}
                        >
                          {formatCount(objectCount)}
                        </KPIBigValue>
                      </KPIValueSlot>
                    </TooltipTrigger>
                    <TooltipContent>
                      Objekttyp: {objectType || "Alle"}
                    </TooltipContent>
                  </Tooltip>
                  {periodLabel && <KPICaption>{periodLabel}</KPICaption>}
                </KPIValueColumn>

                {effectiveComparisonEnabled && (
                  <KPIValueColumn>
                    {trend ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <KPITrendSlot className="cursor-default">
                            <KPITrendBadge trend={trend}>{formatTrendPercent(trend)}</KPITrendBadge>
                          </KPITrendSlot>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div>Aktuell: {formatCount(objectCount)}</div>
                          <div>Vorperiode: {formatCount(previousObjectCount)}</div>
                          {previousPeriodLabel && <div>{previousPeriodLabel}</div>}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <KPITrendSlot>
                        <KPICaption>Kein Vergleichswert verfügbar</KPICaption>
                      </KPITrendSlot>
                    )}

                    {trend && comparisonCaption && <KPICaption>{comparisonCaption}</KPICaption>}
                  </KPIValueColumn>
                )}
              </KPIValueRow>
            </TooltipProvider>
          </div>
        </TileContent>
      )}

      {/* Settings Modal */}
      <TileSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={handleSettingsOpenChange}
        title="Object Count KPI Settings"
        getInitialValues={() => ({
          componentTitle, objectType,
          ampelEnabled, ampelLower, ampelUpper, ampelColorRed, ampelColorYellow, ampelColorGreen,
          periodPreset, customStart, customEnd, showPeriodLabel, comparisonEnabled, trendDirection,
        })}
        onApply={(values) => {
          setComponentTitle(values.componentTitle);
          setObjectType(values.objectType);
          setAmpelEnabled(values.ampelEnabled);
          setAmpelLower(values.ampelLower);
          setAmpelUpper(values.ampelUpper);
          setAmpelColorRed(values.ampelColorRed);
          setAmpelColorYellow(values.ampelColorYellow);
          setAmpelColorGreen(values.ampelColorGreen);
          setPeriodPreset(values.periodPreset);
          setCustomStart(values.customStart);
          setCustomEnd(values.customEnd);
          setShowPeriodLabel(values.showPeriodLabel);
          setComparisonEnabled(values.comparisonEnabled);
          setTrendDirection(values.trendDirection);
          if (onTitleChange && values.componentTitle) {
            onTitleChange(values.componentTitle);
          }
          applyDraftFilters();
          setPendingApply(true);
        }}
        applyButtonText="Apply & Load"
        isApplyDisabled={false}
      >
        {({ values, setField }) => (
          <ObjectCountKPISettings
            componentTitle={values.componentTitle}
            onTitleChange={(v) => setField("componentTitle", v)}
            objectType={values.objectType}
            onObjectTypeChange={(v) => setField("objectType", v)}
            ampelEnabled={values.ampelEnabled}
            ampelLower={values.ampelLower}
            ampelUpper={values.ampelUpper}
            ampelColorRed={values.ampelColorRed}
            ampelColorYellow={values.ampelColorYellow}
            ampelColorGreen={values.ampelColorGreen}
            onAmpelEnabledChange={(v) => setField("ampelEnabled", v)}
            onAmpelLowerChange={(v) => setField("ampelLower", v)}
            onAmpelUpperChange={(v) => setField("ampelUpper", v)}
            onAmpelColorRedChange={(v) => setField("ampelColorRed", v)}
            onAmpelColorYellowChange={(v) => setField("ampelColorYellow", v)}
            onAmpelColorGreenChange={(v) => setField("ampelColorGreen", v)}
            availableObjectTypes={availableObjectTypes}
            localFilters={localFilters}
            updateLocalFilter={updateLocalFilter}
            activeFile={activeFile}
            loading={loading}
            periodPreset={values.periodPreset}
            onPeriodPresetChange={(v) => setField("periodPreset", v)}
            customStart={values.customStart}
            onCustomStartChange={(v) => setField("customStart", v)}
            customEnd={values.customEnd}
            onCustomEndChange={(v) => setField("customEnd", v)}
            showPeriodLabel={values.showPeriodLabel}
            onShowPeriodLabelChange={(v) => setField("showPeriodLabel", v)}
            comparisonEnabled={values.comparisonEnabled}
            onComparisonEnabledChange={(v) => setField("comparisonEnabled", v)}
            trendDirection={values.trendDirection}
            onTrendDirectionChange={(v) => setField("trendDirection", v)}
          />
        )}
      </TileSettingsModal>
    </BaseTile>
  );
};

export default ObjectCountKPI;
